// src/components/Sidebar.js
import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import "./Sidebar.css";

const NAV = [
  {
    path: "/dashboard",
    icon: "📊",
    label: "ಡ್ಯಾಶ್‌ಬೋರ್ಡ್",
    roles: ["admin", "principal", "clerk", "viewer"],
  },
  {
    path: "/attendance",
    icon: "✅",
    label: "ಹಾಜರಾತಿ",
    roles: ["admin", "principal", "clerk"],
  },
  { path: "/classes", icon: "🏫", label: "ತರಗತಿ / ಗುಂಪು", roles: ["admin"] },
  {
    path: "/menu-items",
    icon: "🥘",
    label: "ಆಹಾರ ಪದಾರ್ಥಗಳು",
    roles: ["admin"],
  },
  { path: "/config", icon: "⚙️", label: "ಪ್ರಮಾಣ ಸಂರಚನೆ", roles: ["admin"] },
  {
    path: "/stock",
    icon: "🌾",
    label: "ಧಾನ್ಯ ಸ್ಟಾಕ್",
    roles: ["admin", "principal", "clerk"],
  },
  {
    path: "/reports",
    icon: "📋",
    label: "ವರದಿಗಳು",
    roles: ["admin", "principal", "clerk", "viewer"],
  },
  {
    path: "/visitors",
    icon: "👥",
    label: "ಸಂದರ್ಶನ ವರದಿ",
    roles: ["admin", "principal", "clerk"],
  },
  {
    path: "/academic-year",
    icon: "📅",
    label: "ಶೈಕ್ಷಣಿಕ ವರ್ಷ",
    roles: ["admin"],
  },
  { path: "/settings", icon: "🔧", label: "ಸೆಟ್ಟಿಂಗ್ಸ್", roles: ["admin"] },
];

// ── Logout Confirmation Modal ────────────────────────────────
function LogoutConfirm({ onConfirm, onCancel }) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(0,0,0,0.75)",
        backdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
      }}
    >
      <div
        style={{
          background:
            "linear-gradient(135deg,rgba(10,22,40,0.98),rgba(15,28,52,0.98))",
          border: "1px solid rgba(74,222,128,0.2)",
          borderRadius: "20px",
          padding: "2rem",
          maxWidth: "340px",
          width: "100%",
          textAlign: "center",
          boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
        }}
      >
        <div style={{ fontSize: "2.5rem", marginBottom: "0.8rem" }}>🚪</div>
        <h3
          style={{
            fontFamily: "Noto Sans Kannada",
            fontSize: "1.1rem",
            fontWeight: 700,
            color: "#fff",
            marginBottom: "0.6rem",
          }}
        >
          ಲಾಗ್‌ಔಟ್ ಮಾಡುವಿರಾ?
        </h3>
        <p
          style={{
            fontFamily: "Noto Sans Kannada",
            fontSize: "0.8rem",
            color: "rgba(255,255,255,0.5)",
            marginBottom: "1.5rem",
            lineHeight: 1.6,
          }}
        >
          ನೀವು ಲಾಗ್‌ಔಟ್ ಆದ ನಂತರ ಹಿಂದಿನ ಪುಟಕ್ಕೆ ಹೋದರೆ ಮತ್ತೆ ಲಾಗಿನ್
          ಮಾಡಬೇಕಾಗುತ್ತದೆ.
        </p>
        <div style={{ display: "flex", gap: "0.8rem" }}>
          <button
            style={{
              flex: 1,
              padding: "0.7rem",
              borderRadius: "10px",
              background: "rgba(255,255,255,0.07)",
              border: "1px solid rgba(255,255,255,0.12)",
              color: "rgba(255,255,255,0.7)",
              fontFamily: "Noto Sans Kannada",
              fontSize: "0.88rem",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            onClick={onCancel}
          >
            ರದ್ದು
          </button>
          <button
            style={{
              flex: 1,
              padding: "0.7rem",
              borderRadius: "10px",
              background: "linear-gradient(135deg,#dc2626,#b91c1c)",
              border: "none",
              color: "#fff",
              fontFamily: "Noto Sans Kannada",
              fontSize: "0.88rem",
              fontWeight: 700,
              cursor: "pointer",
              boxShadow: "0 4px 16px rgba(220,38,38,0.4)",
              transition: "all 0.2s",
            }}
            onClick={onConfirm}
          >
            🚪 ಲಾಗ್‌ಔಟ್
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Sidebar({ open, onClose }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const confirmLogout = () => {
    // 1. Call logout (clears localStorage + sets sessionStorage flag)
    logout();
    // 2. Navigate to login with replace:true
    //    replace = removes current entry from history stack
    //    Back button can't return to dashboard
    navigate("/login", { replace: true });
    toast.success("ಲಾಗ್‌ಔಟ್ ಯಶಸ್ವಿ 👋", { duration: 2500 });
    setShowLogoutConfirm(false);
    onClose();
  };

  const allowed = NAV.filter((n) => n.roles.includes(user?.role));

  const roleLabel =
    {
      admin: "ಆಡಳಿತ",
      principal: "ಮುಖ್ಯೋಪಾಧ್ಯಾಯರು",
      clerk: "ಗುಮಾಸ್ತರು",
      viewer: "ವೀಕ್ಷಕ",
    }[user?.role] || user?.role;

  return (
    <>
      {/* Mobile overlay */}
      {open && <div className="sidebar-backdrop" onClick={onClose} />}

      <aside className={`sidebar ${open ? "sidebar-open" : ""}`}>
        {/* Brand */}
        <div className="sidebar-brand">
          <span className="brand-icon">🍚</span>
          <div className="brand-text">
            <span className="brand-title">ಅಕ್ಷರ ದಾಸೋಹ</span>
            <span className="brand-sub">ನಿರ್ವಹಣಾ ವ್ಯವಸ್ಥೆ</span>
          </div>
          <button className="sidebar-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* User info */}
        <div className="sidebar-user">
          <div className="user-avatar">
            {user?.full_name_kannada?.[0] || "👤"}
          </div>
          <div className="user-info">
            <span className="user-name">{user?.full_name_kannada}</span>
            <span className={`user-role role-${user?.role}`}>{roleLabel}</span>
          </div>
        </div>

        <div className="sidebar-divider" />

        {/* Nav links */}
        <nav className="sidebar-nav">
          {allowed.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `nav-item ${isActive ? "nav-active" : ""}`
              }
              onClick={onClose}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-label">{item.label}</span>
              <span className="nav-arrow">›</span>
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-spacer" />

        {/* Logout button — prominent red at bottom */}
        <div className="sidebar-logout-wrap">
          <button
            className="sidebar-logout-btn"
            onClick={() => setShowLogoutConfirm(true)}
          >
            <span className="logout-icon">🚪</span>
            <span className="logout-label">ಲಾಗ್‌ಔಟ್</span>
            <span className="logout-arrow">→</span>
          </button>
        </div>
      </aside>

      {/* Logout confirmation modal */}
      {showLogoutConfirm && (
        <LogoutConfirm
          onConfirm={confirmLogout}
          onCancel={() => setShowLogoutConfirm(false)}
        />
      )}
    </>
  );
}
